import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useLanguageStore } from '../../store/languageStore';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
}) => {
  const { isRTL } = useLanguageStore();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-full mx-4',
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          {/* Backdrop with blur effect */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
            style={{
              zIndex: 9999,
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.15) 50%, rgba(139, 92, 246, 0.1) 100%)',
              backdropFilter: 'blur(20px) saturate(180%)',
            }}
            onClick={onClose}
          >
            {/* Animated background gradient */}
            <motion.div
              className="position-absolute w-100 h-100"
              style={{
                background: 'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.15) 0%, transparent 50%)',
                zIndex: 0,
              }}
              animate={{
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
              }}
            />

            {/* Modal Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className={`
                rounded-5 shadow-lg border-0 ${sizeClasses[size]} w-100
                overflow-hidden d-flex flex-column position-relative
              `}
              style={{
                maxHeight: '90vh',
                maxWidth: size === 'sm' ? '28rem' : size === 'md' ? '32rem' : size === 'lg' ? '42rem' : size === 'xl' ? '56rem' : '100%',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(40px) saturate(180%)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 30px 100px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.2) inset, 0 0 200px rgba(59, 130, 246, 0.15)',
                zIndex: 1,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Shimmer Effect */}
              <motion.div
                className="position-absolute w-100 h-100"
                style={{
                  top: 0,
                  left: '-100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                  zIndex: 1,
                }}
                animate={{
                  left: ['-100%', '200%'],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  repeatDelay: 2,
                }}
              />

              {/* Header */}
              {title && (
                <motion.div
                  className="d-flex align-items-center justify-content-between p-4 position-relative"
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)',
                    zIndex: 2,
                  }}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  {/* Header glow effect */}
                  <motion.div
                    className="position-absolute w-100 h-100"
                    style={{
                      background: 'radial-gradient(circle at center, rgba(255, 255, 255, 0.2) 0%, transparent 70%)',
                      top: 0,
                      left: 0,
                      zIndex: 0,
                    }}
                    animate={{
                      opacity: [0.3, 0.5, 0.3],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                    }}
                  />

                  <motion.h2
                    className="mb-0 text-white fw-bold position-relative"
                    style={{ 
                      fontSize: '1.5rem', 
                      letterSpacing: '0.01em',
                      zIndex: 1,
                    }}
                    initial={{ opacity: 0, x: isRTL ? 10 : -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    {title}
                  </motion.h2>
                  {showCloseButton && (
                    <motion.button
                      onClick={onClose}
                      className="btn btn-link p-2 rounded-circle text-white position-relative"
                      style={{
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1,
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                      whileHover={{ 
                        scale: 1.1, 
                        rotate: 90, 
                        background: 'rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                      }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <X className="h-5 w-5" strokeWidth={2.5} />
                    </motion.button>
                  )}
                </motion.div>
              )}

              {/* Content */}
              <motion.div
                className="p-5 overflow-y-auto flex-grow-1 position-relative"
                style={{ 
                  backgroundColor: 'rgba(248, 250, 252, 0.8)',
                  scrollbarWidth: 'thin',
                  zIndex: 2,
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {children}
              </motion.div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
